import React, { useMemo, useState } from "react";
import { apiRequest, phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

/**
 * Who-does-what matrix for group managers ("Manager Options" tab).
 *
 * The manager detail panel shows these flags one person at a time, which makes
 * "who is tagged in check-ins?" a click-through-everyone exercise. This lays the
 * whole roster out at once — managers down the side, flags across the top — and
 * lets you toggle a cell in place instead of opening each person's modal.
 *
 * PH Admin viewers also get a Slack column (Dialpad status sync): connecting
 * your own row redirects straight to Slack's consent screen; connecting someone
 * else's row copies a 10-minute link to send them, because the OAuth flow binds
 * whichever Slack account is logged into the browser that completes it.
 */

// Grouped so access rights and auto-assignment don't read as the same kind of thing.
const COLUMN_GROUPS = [
  {
    label: "Internal",
    columns: [
      { key: "IsPHAdmin", label: "PH Admin", color: "#2563eb" },
      { key: "IsZephyrAdmin", label: "Zephyr", color: "#38184f" },
      { key: "is_conferences_user", label: "Conferences", color: "#d97706" },
      { key: "is_marketing_user", label: "Marketing", color: "#c2410c" },
    ],
  },
  {
    label: "External portals",
    columns: [
      { key: "IsHubMedAdmin", label: "HubMed", color: "#5b21b6" },
      { key: "IsHolaAdmin", label: "Hola", color: "#0891b2" },
      { key: "IsMedmateAdmin", label: "Medmate", color: "#0d9488" },
    ],
  },
  {
    label: "Auto-tagged in",
    columns: [
      { key: "receives_checkin_tasks", label: "Check-ins", color: "#b91c1c" },
    ],
  },
  {
    label: "Email opt-ins",
    columns: [
      { key: "receives_enquiry_email", label: "Enquiries", color: "#65a30d" },
      { key: "receives_support_email", label: "Support", color: "#ea580c" },
    ],
  },
];

// Sits in the Internal group after Marketing, PH-Admin viewers only.
const SLACK_COLUMN = { key: "slackConnected", label: "Slack", color: "#4A154B", slack: true };

// The update endpoint replaces the whole flag set, so every toggle has to send
// the manager's current values with the one change applied.
const FLAG_KEYS = [
  "IsPHAdmin",
  "IsHubMedAdmin",
  "IsHolaAdmin",
  "IsMedmateAdmin",
  "IsZephyrAdmin",
  "is_conferences_user",
  "receives_enquiry_email",
  "receives_support_email",
  "receives_checkin_tasks",
  "is_marketing_user",
];

const rowId = (m) => m.Id ?? m.id;

function SlackLogo({ className }) {
  return (
    <svg viewBox="0 0 122.8 122.8" className={className} aria-hidden="true">
      <path d="M25.8 77.6c0 7.1-5.8 12.9-12.9 12.9S0 84.7 0 77.6s5.8-12.9 12.9-12.9h12.9v12.9zm6.5 0c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V77.6z" fill="#e01e5a" />
      <path d="M45.2 25.8c-7.1 0-12.9-5.8-12.9-12.9S38.1 0 45.2 0s12.9 5.8 12.9 12.9v12.9H45.2zm0 6.5c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H12.9C5.8 58.1 0 52.3 0 45.2s5.8-12.9 12.9-12.9h32.3z" fill="#36c5f0" />
      <path d="M97 45.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9-5.8 12.9-12.9 12.9H97V45.2zm-6.5 0c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V12.9C64.7 5.8 70.5 0 77.6 0s12.9 5.8 12.9 12.9v32.3z" fill="#2eb67d" />
      <path d="M77.6 97c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9-12.9-5.8-12.9-12.9V97h12.9zm0-6.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H77.6z" fill="#ecb22e" />
    </svg>
  );
}

function Cell({ checked, color, busy, onToggle, label }) {
  return (
    <td className="px-2 py-1.5 text-center">
      <button
        type="button"
        onClick={onToggle}
        disabled={busy}
        aria-label={label}
        aria-pressed={checked}
        className={`w-6 h-6 rounded-md border transition-colors inline-flex items-center justify-center ${
          busy ? "opacity-40 cursor-wait" : "cursor-pointer hover:brightness-95"
        }`}
        style={{
          backgroundColor: checked ? color : "#fff",
          borderColor: checked ? color : "#d1d5db",
        }}
      >
        {checked && (
          <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4">
            <path d="M5 10.5l3.5 3.5L15 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
    </td>
  );
}

function SlackCell({ manager, isOwnRow, busy, onConnect, onDisconnect }) {
  const name = manager.Name || "this manager";
  // Dialpad status sync is for the internal team — no Slack connect on
  // non-PH-Admin rows (external portal users etc.).
  if (!manager.IsPHAdmin) {
    return <td className="px-2 py-1.5 text-center" />;
  }
  if (manager.slackConnected) {
    return (
      <td className="px-2 py-1.5 text-center">
        <span className="relative inline-flex group">
          <span
            className="w-6 h-6 rounded-md border border-emerald-300 bg-emerald-50 inline-flex items-center justify-center"
            title={`Slack connected for ${name}`}
          >
            <SlackLogo className="w-3.5 h-3.5" />
          </span>
          {/* Connected badge — swaps to the disconnect × on hover */}
          <span
            aria-hidden="true"
            className="absolute -top-1.5 -right-1.5 inline-flex group-hover:hidden w-4 h-4 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-white pointer-events-none"
          >
            <svg viewBox="0 0 20 20" fill="none" className="w-2.5 h-2.5">
              <path d="M5 10.5l3.5 3.5L15 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <button
            type="button"
            onClick={onDisconnect}
            disabled={busy}
            title={`Disconnect Slack for ${name}`}
            className="absolute -top-1.5 -right-1.5 hidden group-hover:inline-flex w-4 h-4 items-center justify-center rounded-full bg-red-600 text-white text-[10px] leading-none font-bold hover:bg-red-700"
          >
            ×
          </button>
        </span>
      </td>
    );
  }
  return (
    <td className="px-2 py-1.5 text-center">
      <button
        type="button"
        onClick={onConnect}
        disabled={busy}
        title={
          isOwnRow
            ? "Connect your Slack account"
            : `Copy a Slack connect link to send to ${name}`
        }
        className={`w-6 h-6 rounded-md border border-gray-300 bg-white inline-flex items-center justify-center ${
          busy ? "opacity-40 cursor-wait" : "cursor-pointer hover:bg-gray-50"
        }`}
      >
        <SlackLogo className="w-4 h-4" />
      </button>
    </td>
  );
}

const GroupManagerRoster = ({
  managers,
  onUpdated,
  onError,
  onNotice,
  viewerIsPHAdmin = false,
  ownManagerId = null,
}) => {
  const [busyCell, setBusyCell] = useState(null); // `${managerId}:${key}`
  const [search, setSearch] = useState("");

  const columnGroups = useMemo(() => {
    if (!viewerIsPHAdmin) return COLUMN_GROUPS;
    return COLUMN_GROUPS.map((g) =>
      g.label === "Internal" ? { ...g, columns: [...g.columns, SLACK_COLUMN] } : g
    );
  }, [viewerIsPHAdmin]);

  const allColumns = useMemo(
    () => columnGroups.flatMap((g) => g.columns),
    [columnGroups]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return managers;
    return managers.filter((m) =>
      `${m.Name || ""} ${m.Email || ""}`.toLowerCase().includes(q)
    );
  }, [managers, search]);

  const counts = useMemo(() => {
    const out = {};
    allColumns.forEach((c) => {
      out[c.key] = managers.filter((m) => !!m[c.key]).length;
    });
    return out;
  }, [managers, allColumns]);

  const toggle = async (manager, key) => {
    const id = rowId(manager);
    const cellKey = `${id}:${key}`;
    setBusyCell(cellKey);
    try {
      const body = { Id: id, IsActive: manager.IsActive !== false };
      FLAG_KEYS.forEach((k) => { body[k] = !!manager[k]; });
      body[key] = !manager[key];

      await phappsRequest(API_PATHS.GROUP_MANAGERS_UPDATE, "PUT", body);
      onUpdated?.({ ...manager, [key]: !manager[key] });
    } catch (e) {
      onError?.(e.message || "Failed to update that permission.");
    } finally {
      setBusyCell(null);
    }
  };

  const slackConnect = async (manager) => {
    const id = rowId(manager);
    const isOwnRow = ownManagerId != null && Number(id) === Number(ownManagerId);
    setBusyCell(`${id}:slackConnected`);
    try {
      const res = await apiRequest(
        API_PATHS.SLACK_CONNECT_START,
        "POST",
        isOwnRow ? {} : { TargetManagerId: id }
      );
      const url = res?.url;
      if (!url) throw new Error("No authorise URL returned.");
      if (isOwnRow) {
        window.location.href = url;
        return; // navigating away — leave the cell busy
      }
      await navigator.clipboard.writeText(url);
      onNotice?.(
        `Link copied — send it to ${manager.Name || "them"} to open in their own browser. Valid for 10 minutes.`
      );
    } catch (e) {
      onError?.(e.message || "Failed to create Slack connect link.");
    } finally {
      setBusyCell(null);
    }
  };

  const slackDisconnect = async (manager) => {
    const id = rowId(manager);
    if (
      !window.confirm(
        `Disconnect Slack for ${manager.Name || "this manager"}? Dialpad status sync will stop for them.`
      )
    )
      return;
    setBusyCell(`${id}:slackConnected`);
    try {
      await apiRequest(API_PATHS.SLACK_CONNECT_DISCONNECT, "POST", {
        TargetManagerId: id,
      });
      onUpdated?.({ ...manager, slackConnected: false });
    } catch (e) {
      onError?.(e.message || "Failed to disconnect Slack.");
    } finally {
      setBusyCell(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm overflow-hidden flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3 shrink-0">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Manager Options</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Who has access to what, and who work gets auto-assigned to. Click a cell to change it.
          </p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search managers…"
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 w-56"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <table className="min-w-full text-sm border-separate border-spacing-0">
          <thead className="sticky top-0 z-10">
            <tr>
              <th
                rowSpan={2}
                className="sticky left-0 z-20 bg-gray-100 text-left px-3 py-2 font-semibold text-gray-700 border-b border-r border-gray-200"
              >
                Manager
              </th>
              {columnGroups.map((g) => (
                <th
                  key={g.label}
                  colSpan={g.columns.length}
                  className="bg-gray-100 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 border-b border-l border-gray-200 text-center"
                >
                  {g.label}
                </th>
              ))}
            </tr>
            <tr>
              {columnGroups.flatMap((g, gi) =>
                g.columns.map((c, ci) => (
                  <th
                    key={c.key}
                    className={`bg-gray-50 px-2 py-2 text-[11px] font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap ${
                      ci === 0 && gi > 0 ? "border-l" : ""
                    }`}
                  >
                    <span className="block">{c.label}</span>
                    <span className="block text-[10px] font-normal text-gray-400">{counts[c.key]}</span>
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={allColumns.length + 1} className="px-3 py-8 text-center text-gray-500">
                  No managers match this filter.
                </td>
              </tr>
            ) : (
              rows.map((m) => {
                const id = rowId(m);
                const inactive = m.IsActive === false;
                return (
                  <tr key={id} className={`hover:bg-blue-50/40 ${inactive ? "opacity-50" : ""}`}>
                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5 border-b border-r border-gray-100 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{m.Name || "—"}</span>
                      {inactive && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">inactive</span>
                      )}
                      <span className="block text-xs text-gray-400">{m.Email || ""}</span>
                    </td>
                    {allColumns.map((c) =>
                      c.slack ? (
                        <SlackCell
                          key={c.key}
                          manager={m}
                          isOwnRow={ownManagerId != null && Number(id) === Number(ownManagerId)}
                          busy={busyCell === `${id}:${c.key}`}
                          onConnect={() => slackConnect(m)}
                          onDisconnect={() => slackDisconnect(m)}
                        />
                      ) : (
                        <Cell
                          key={c.key}
                          checked={!!m[c.key]}
                          color={c.color}
                          busy={busyCell === `${id}:${c.key}`}
                          onToggle={() => toggle(m, c.key)}
                          label={`${c.label} for ${m.Name || id}`}
                        />
                      )
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupManagerRoster;
